import { defineStore } from "pinia";
import { FetchService } from "@/services/fetch.service";
import { authService } from "@/services/auth.service";
import { useRoute } from "vue-router";
import type { GetLogin, GetMe } from "@/api_types/teacher";
import router from "@/router";
import { useSchoolStore } from "./school";

export type Level = "parent" | "teacher" | "school" | "district" | "support";
export type TokenData = {
  iss: string;
  iat: number;
  exp: number;
  nbf: number;
  jti: string;
  sub: string;
  prv: string;
  school_id: number;
  district_id: number;
  district: string;
  integration: string;
  uid: string;
  authentication_type: Level;
  authentication_level: number;
  roles: string[] | Record<string, string>;
  policies: Array<string | number>;
};

// useStore could be anything like useUser, useCart
// the first argument is a unique id of the store across your application

export const useAuthenticationStore = defineStore("authentication", {
  state: () => {
    return {
      jwt: null as GetLogin | null,
      // Seed roles/policies so templates never crash before login
      jwtData: { roles: [], policies: [] } as unknown as TokenData,
      modal: false,
      me: {} as GetMe,
      level: "parent" as Level,
      resetPassword: false as boolean,
      account: ""
    };
  },
  actions: {
    getName() {
      return this.me?.teacher_fname + " " + this.me?.teacher_lname;
    },

    // --- Role helpers ---

    hasRole(role: string): boolean {
      return normalizeRoles(this.jwtData.roles).includes(role);
    },

    hasAnyRole(roles: string[]): boolean {
      const tokenRoles = normalizeRoles(this.jwtData.roles);
      return roles.some((r) => tokenRoles.includes(r));
    },

    hasPolicy(policy: string): boolean {
      const tokenPolicies = this.jwtData.policies ?? [];

      for (const p of tokenPolicies) {
        if (typeof p === "string") {
          // Fast-path exact match.
          if (p === policy) return true;
          // Wildcard implication: `school.*` should satisfy `school.events.*` etc.
          if (p.includes("*") && wildcardMatch(p, policy)) return true;
        }
      }
      return false;
    },

    /**
     * Default path after school/district login or SSO. Uses the same `requiredPolicy`
     * strings as `router.ts`: prefer events home, then directory (for users without
     * events access), then other single-area routes, then Help (auth-only).
     */
    getSchoolLandingPath(account: string): string {
      const base = `/${account}/school`;
      if (this.hasPolicy("school.events.general.read")) return base;
      if (this.hasPolicy("school.directory.classes.read")) return `${base}/directory`;
      if (this.hasPolicy("school.integration.read")) return `${base}/integration`;
      if (this.hasPolicy("school.profile.read")) return `${base}/settings`;
      if (this.hasPolicy("school.users.read")) return `${base}/user-management`;
      return `${base}/help`;
    },

    /**
     * Primary guard used by route guards and v-if directives.
     *
     * Falls back to authentication_type during the backfill transition period —
     * users whose user_role rows haven't been seeded yet are still allowed through.
     *
     * Post-backfill (coordinated with API `CheckRole`):
     * Remove this fallback and the empty-`roles` branch in ptc-wizard-api
     * `CheckRole` after `user_role` is confirmed for every tenant.
     */
    canAccess(requiredRoles: string[]): boolean {
      const roles = normalizeRoles(this.jwtData.roles);
      if (roles.length === 0) {
        // --- SAFE FALLBACK (remove after full user_role backfill) ---
        const typeMap: Record<string, string> = {
          school: "school",
          district: "district",
          support: "admin",
          teacher: "teacher",
          parent: "parent"
        };
        const mapped = typeMap[this.jwtData.authentication_type] ?? "";
        return requiredRoles.includes(mapped);
        // --- END SAFE FALLBACK ---
      }
      return this.hasAnyRole(requiredRoles);
    },

    // --- End role helpers ---
    async fetchProfile() {
      const profile = await FetchService.fetchData("/me", "get");
      if (this.level == 'support'){
        this.me = profile || {};
      }else{
        const schoolStore = useSchoolStore();
        if (!schoolStore.school || schoolStore.account !== this.account)
          await schoolStore.loadSchool(this.account);
        const integration = schoolStore.school?.integration;
        if (
          profile &&
          typeof profile !== "string" &&
          integration == "none" &&
          this.level === "teacher"
        ) {
          const [guardians, students] = await Promise.all([this.getGuardians(), this.getStudents()]);
          if (guardians?.length && typeof guardians !== "string") profile.guardians = guardians;
          if (students?.length && typeof students !== "string") profile.students = students;
        }
        this.me = profile || {};
  
        if (this.me.students?.length == 0) {
          this.modal = true;
        }
      }
    },
    async googleUrl(district_id, school_id, type) {
      const url = await FetchService.fetchRaw(
        "/google/url?district_id=" + district_id + "&school_id=" + school_id + "&type=" + type ||
          this.level
      );
      return url;
    },
    setJWT(res) {
      this.jwt = res?.access_token ? res : null;
      if (this.jwt) {
        this.jwtData = authService.parseJwt(res.access_token);
        localStorage.setItem(this.getTokenName(), JSON.stringify(res));
        return this.fetchProfile();
      } else {
        this.jwtData = {} as any;
        localStorage.setItem(this.getTokenName(), "{}");
      }
    },
    getTokenName(level?: string) {
      if (level == 'support' || this.level == 'support'){
        return `ptcwizard_${level || this.level}_jwt`;
      }else{
        return `ptcwizard_${this.account || useRoute()?.params?.account}_${level || this.level}_jwt`;
      }
    },
    isLoggedIn() {
      const jwt = this.getToken();
      if (!jwt?.access_token) return false;
      const parsed = authService.parseJwt(jwt.access_token);
      const expirationDate = new Date(parsed.exp * 1000);
      const currentDate = new Date();
      if (currentDate < expirationDate) {
        return true;
      } else {
        return false;
      }
    },

    async login(user, isDistrict?: boolean) {
      const res = await authService.login(user, isDistrict ? "district" : this.level);
      await this.setJWT(res);
      this.resetPassword = res.reset_password ? res.reset_password : false;
      return !!res;
    },

    async loginSso() {
      const route = useRoute();
      const hash = route.query.token || route.hash?.replace("#token=", "") || null;
      const token = authService.loginFromHash(hash);
      this.setJWT(token);
      return !!token;
    },
    loadToken() {
      const data = localStorage.getItem(this.getTokenName());
      this.setJWT(data ? JSON.parse(data) : null);
    },
    resetPasswordRequired() {
      return this.resetPassword;
    },
    getToken() {
      if (this.jwt) return this.jwt;
      else {
        this.loadToken();
        return this.jwt;
      }
    },

    logout() {
      this.setJWT(null);
    },

    pushRoute(to: string) {
      if (!this.level) router.push(to || "/");
    if (this.level == 'support'){
      router.push(`/${this.level}/${to.replace(/^\//, "")}`);
    } else   if (this.level == "parent"){
        router.push(`/${this.account}/${to.replace(/^\//, "")}`);
      }else{
        router.push(`/${this.account}/${this.level}/${to.replace(/^\//, "")}`);
      }
    },
    getGuardians() {
      return FetchService.fetchData("/guardians", "get");
    },
    getStudents() {
      return FetchService.fetchData("/students", "get");
    }
  }
});

function wildcardMatch(pattern: string, value: string): boolean {
  // Convert a glob-like policy pattern (e.g. `school.events.*`) to a regex.
  // `*` matches any sequence of characters (including dots).
  const escaped = pattern.replace(/[.+?^${}()|[\]\\]/g, "\\$&");
  const regexStr = "^" + escaped.replace(/\*/g, ".*") + "$";
  return new RegExp(regexStr).test(value);
}

function normalizeRoles(
  roles: TokenData["roles"] | undefined
): string[] {
  if (!roles) return [];
  if (Array.isArray(roles)) return roles.filter((role) => typeof role === "string");
  if (typeof roles === "object") {
    return Object.values(roles).filter((role) => typeof role === "string");
  }
  return [];
}
